import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import Layout from "../../Components/Layout";
import AIAnswerCard from "../../Components/AISearch/AIAnswerCard";
import SearchResultsList from "../../Components/AISearch/SearchResultsList";
import { KBContext } from "../../Context";
import { useKeywordSearchQuery, useAiSearchMutation } from "../../hooks/apis";
import { FTR_KB_AI_SEARCH_CONTINUE_CONVERSATION } from "../../Constants";
import { isFeatureEnabled } from "../../utils";

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** True when AI response indicates no data found (hide sources and results list). */
function isNoDataResponse(content, sources = [], searchResults = []) {
    const noDataPhrases = /couldn't find|could not find|didn't find|no (data|results?|information|articles?)\s*(found)?/i;
    const contentSaysNoData = content && noDataPhrases.test(content);
    const hasNoSourcesOrResults = (!sources || sources.length === 0) && (!searchResults || searchResults.length === 0);
    return contentSaysNoData || hasNoSourcesOrResults;
}

/**
 * /search page — AI-powered search via POST v1/article/search (Bearer token).
 */
export default function SearchPage() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { availableFeatures } = useContext(KBContext);
    const query = searchParams.get("q") || "";
    const product = searchParams.get("product") || undefined;
    const isContinueConversationEnabled = isFeatureEnabled(
        FTR_KB_AI_SEARCH_CONTINUE_CONVERSATION,
        availableFeatures
    );

    const [displayedText, setDisplayedText] = useState("");
    const [sources, setSources] = useState([]);
    const [searchError, setSearchError] = useState(null);
    const [followUps, setFollowUps] = useState([]);

    const aiSearchMutation = useAiSearchMutation();
    const searchTrigger = `${query}::${product ?? ""}::${location.state?.searchKey ?? ""}`;
    const prevTriggerRef = useRef("");

    // --- session_id & conversation_history for multi-turn AI search ---
    const sessionIdRef = useRef(crypto.randomUUID());
    const conversationHistoryRef = useRef([]);

    const {
        data: keywordResults = [],
        isLoading: isKeywordLoading,
    } = useKeywordSearchQuery(query, product);

    useEffect(() => {
        if (!query) return;
        if (searchTrigger === prevTriggerRef.current) return;
        prevTriggerRef.current = searchTrigger;
        setSearchError(null);
        setDisplayedText("");
        setSources([]);
        setFollowUps([]);

        // New primary query → fresh session
        sessionIdRef.current = crypto.randomUUID();
        conversationHistoryRef.current = [];

        aiSearchMutation.mutate(
            {
                query,
                product: product || null,
                session_id: sessionIdRef.current,
                conversation_history: [],
            },
            {
                onSuccess: (data) => {
                    if (data?.content) {
                        setDisplayedText(data.content);
                        conversationHistoryRef.current = [
                            { question: query, answer: data.content },
                        ].slice(-5);
                    }
                    if (data?.sources) setSources(data.sources);
                },
                onError: () => setSearchError("Search failed. Please try again."),
            }
        );
    }, [query, product, searchTrigger]);

    useEffect(() => {
        if (isContinueConversationEnabled || followUps.length < 1) return;
        setFollowUps([]);
    }, [isContinueConversationEnabled, followUps.length]);

    const isFollowUpLoading = isContinueConversationEnabled
        ? followUps.some((f) => f.isLoading)
        : false;
    const isLoading = aiSearchMutation.isLoading;

    const followUpSearch = useCallback(
        (question, { onChunk, onDone } = {}) => {
            const session_id = sessionIdRef.current;
            const conversation_history = conversationHistoryRef.current.slice(-5);

            aiSearchMutation.mutate(
                {
                    query: question,
                    product: product || null,
                    session_id,
                    conversation_history,
                },
                {
                    onSuccess: (data) => {
                        if (data?.sources) setSources(data.sources);
                        if (data?.content) {
                            onChunk(data.content);
                            conversationHistoryRef.current = [
                                ...conversationHistoryRef.current,
                                { question, answer: data.content },
                            ].slice(-5);
                        }
                        onDone();
                    },
                    onError: () => onDone(),
                }
            );
        },
        [aiSearchMutation, product]
    );

    const handleFollowUp = useCallback(
        (questionText) => {
            if (!isContinueConversationEnabled) return;
            const followUpKey = Date.now();
            setFollowUps((prev) => [
                ...prev,
                {
                    key: followUpKey,
                    question: questionText,
                    displayedText: "",
                    isLoading: true,
                },
            ]);
            followUpSearch(questionText, {
                onChunk: (chunk) => {
                    setFollowUps((prev) =>
                        prev.map((f) =>
                            f.key === followUpKey
                                ? { ...f, displayedText: f.displayedText + chunk }
                                : f
                        )
                    );
                },
                onDone: () => {
                    setFollowUps((prev) =>
                        prev.map((f) =>
                            f.key === followUpKey
                                ? { ...f, isLoading: false }
                                : f
                        )
                    );
                },
            });
        },
        [followUpSearch, isContinueConversationEnabled]
    );

    if (!query) {
        return (
            <Layout>
                <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
                    <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: 18, color: "#94a3b8" }}>
                        Enter a search query to get started.
                    </Typography>
                </Box>
            </Layout>
        );
    }

    const showLoading = isLoading && !displayedText && !searchError;
    const hasNoData = isNoDataResponse(displayedText, sources, keywordResults);

    return (
        <Layout>
            <Box
                sx={{
                    maxWidth: 1000,
                    mx: "auto",
                    fontFamily: "'Inter', sans-serif",
                }}
            >
                {searchError && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "#fef2f2", borderRadius: 1 }}>
                        <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#b91c1c" }}>
                            {searchError}
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        animation: `${fadeSlideUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
                        animationDelay: "0.1s",
                    }}
                >
                    <AIAnswerCard
                        query={query}
                        displayedText={displayedText}
                        isLoading={showLoading || isLoading}
                        sources={sources}
                        followUps={isContinueConversationEnabled ? followUps : []}
                        onFollowUp={handleFollowUp}
                        isFollowUpLoading={isContinueConversationEnabled ? isFollowUpLoading : false}
                        disableFollowUp={!isContinueConversationEnabled}
                        showContinueConversation={isContinueConversationEnabled}
                        showSources={!hasNoData}
                    />
                </Box>

                {!hasNoData && (
                    <Box
                        sx={{
                            animation: `${fadeSlideUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both`,
                            animationDelay: "0.25s",
                        }}
                    >
                        <SearchResultsList results={keywordResults} query={query} />
                    </Box>
                )}
            </Box>
        </Layout>
    );
}
